import React, { useEffect, useState } from 'react';
import { Person } from '../../types';
import type { CandidateDetailsResponse, ReviewStatus } from '../../types/candidateDetails';
import { peopleApi } from '../../services/api';
import {
  listPoolMembershipsForPerson,
  removeMember as removePoolMember,
  type PersonPoolMembership,
} from '../../services/candidatePoolsApi';
import { useAuth } from '../../hooks/useAuth';

const WRITABLE_POOL_ROLES = new Set(['owner', 'editor']);

export interface ProfileAdminTabProps {
  person: Person;
  details?: CandidateDetailsResponse;
  isAdmin?: boolean;
  onPersonUpdated?: (updated: Person) => void;
  onArchived?: () => void;
  onNavigateToCandidatePool?: (poolId: number) => void;
}

export const ProfileAdminTab: React.FC<ProfileAdminTabProps> = ({
  person,
  details,
  isAdmin = false,
  onPersonUpdated,
  onArchived,
  onNavigateToCandidatePool,
}) => {
  const { canEditEmployeeLinkedIn } = useAuth();
  const canArchive = canEditEmployeeLinkedIn || isAdmin;
  const [memberships, setMemberships] = useState<PersonPoolMembership[] | null>(null);
  const [membershipsError, setMembershipsError] = useState<string | null>(null);
  const [busyPoolId, setBusyPoolId] = useState<number | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const recruitingMemberships =
    memberships?.filter((m) => !m.is_internal_directory) ?? [];

  useEffect(() => {
    if (person.id == null) return;
    let cancelled = false;
    setMemberships(null);
    setMembershipsError(null);
    listPoolMembershipsForPerson(person.id)
      .then((rows) => {
        if (!cancelled) setMemberships(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setMembershipsError(e instanceof Error ? e.message : 'Failed to load memberships');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [person.id]);

  const handleRemoveFromPool = async (m: PersonPoolMembership) => {
    if (person.id == null) return;
    const isLastRecruiting =
      recruitingMemberships.length === 1 && recruitingMemberships[0]?.pool_id === m.pool_id;
    const msg = isLastRecruiting
      ? `Remove from "${m.pool_name}"? This is their last recruiting pool — they will be archived and hidden from all searches.`
      : `Remove from pool "${m.pool_name}"? They will stay in other pools.`;
    if (!window.confirm(msg)) return;
    setActionError(null);
    setBusyPoolId(m.pool_id);
    try {
      const result = await removePoolMember(m.pool_id, person.id);
      setMemberships(await listPoolMembershipsForPerson(person.id));
      if (result.archived) onArchived?.();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Remove from pool failed');
    } finally {
      setBusyPoolId(null);
    }
  };

  const handleArchive = async () => {
    if (person.id == null) return;
    const reason = window.prompt(
      'Archive this person? Optional reason (leave empty to skip):'
    );
    if (reason === null) return;
    if (!window.confirm('Archive globally? They will be hidden from all searches (data kept).')) {
      return;
    }
    setActionError(null);
    setArchiveBusy(true);
    try {
      await peopleApi.archivePerson(person.id, reason);
      onArchived?.();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Archive failed');
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleUnarchive = async () => {
    if (person.id == null) return;
    if (!window.confirm('Restore this person to the active directory?')) return;
    setActionError(null);
    setArchiveBusy(true);
    try {
      const { person: restored } = await peopleApi.unarchivePerson(person.id);
      onPersonUpdated?.(restored);
      setMemberships(await listPoolMembershipsForPerson(person.id));
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Unarchive failed');
    } finally {
      setArchiveBusy(false);
    }
  };

  const confidenceEntries = details?.parseConfidence
    ? Object.entries(details.parseConfidence).sort((a, b) => a[0].localeCompare(b[0]))
    : [];

  return (
    <div className="space-y-6" data-testid="candidate-profile-admin">
      {person.IsArchived && (
        <div
          className="rounded-lg border border-amber-500/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-100"
          data-testid="candidate-profile-admin-archived-banner"
        >
          <p className="font-semibold">Archived</p>
          <p className="text-xs text-amber-200/90 mt-1">
            Hidden from search and active lists
            {person.ArchivedAt ? ` since ${formatDateIso(person.ArchivedAt) ?? person.ArchivedAt}` : ''}
            {person.ArchivedBy ? ` by ${person.ArchivedBy}` : ''}
            {person.ArchiveReason ? ` — ${person.ArchiveReason}` : ''}
          </p>
          {isAdmin && (
            <button
              type="button"
              className="mt-3 px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium disabled:opacity-50"
              disabled={archiveBusy}
              onClick={handleUnarchive}
              data-testid="candidate-profile-admin-unarchive"
            >
              {archiveBusy ? 'Restoring…' : 'Unarchive (admin)'}
            </button>
          )}
        </div>
      )}

      {actionError && (
        <p className="text-xs text-red-400" data-testid="candidate-profile-admin-action-error">
          {actionError}
        </p>
      )}

      {canArchive && !person.IsArchived && (
        <Section title="Archive person">
          <p className="text-xs text-dark-text-secondary mb-3">
            Hides from all searches and active UI. Pool rows are kept for audit.
          </p>
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-red-500/60 text-red-300 hover:bg-red-950/40 text-sm font-medium disabled:opacity-50"
            disabled={archiveBusy}
            onClick={handleArchive}
            data-testid="candidate-profile-admin-archive"
          >
            {archiveBusy ? 'Archiving…' : 'Archive person'}
          </button>
        </Section>
      )}

      <Section title="Pool actions">
        {memberships == null && !membershipsError && (
          <p className="text-xs text-dark-text-secondary">Loading pool memberships…</p>
        )}
        {membershipsError && <p className="text-xs text-red-400">{membershipsError}</p>}
        {memberships != null && memberships.length === 0 && (
          <p className="text-xs text-dark-text-secondary">No pool memberships.</p>
        )}
        {memberships != null && memberships.length > 0 && (
          <ul className="space-y-2" data-testid="candidate-profile-admin-pools">
            {memberships.map((m) => {
              const canRemove =
                !m.is_internal_directory && WRITABLE_POOL_ROLES.has(m.my_role);
              return (
                <li
                  key={m.pool_id}
                  className="flex items-center justify-between gap-3 bg-dark-bg/60 border border-dark-border rounded-lg px-3 py-2"
                >
                  <div className="min-w-0">
                    {onNavigateToCandidatePool ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-blue-400 hover:text-blue-300 truncate"
                        onClick={() => onNavigateToCandidatePool(m.pool_id)}
                      >
                        {m.pool_name}
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-dark-text">{m.pool_name}</span>
                    )}
                    {m.is_internal_directory && (
                      <span className="ml-2 text-[10px] uppercase text-dark-text-secondary">
                        system pool
                      </span>
                    )}
                    <div className="text-xs text-dark-text-secondary mt-0.5">
                      {m.my_role} · added {formatDateIso(m.added_at) ?? m.added_at}
                    </div>
                  </div>
                  {m.is_internal_directory ? (
                    <span className="text-[10px] text-dark-text-secondary shrink-0">—</span>
                  ) : canRemove ? (
                    <button
                      type="button"
                      className="text-xs text-amber-400 hover:text-amber-300 shrink-0 disabled:opacity-50"
                      disabled={busyPoolId === m.pool_id}
                      onClick={() => handleRemoveFromPool(m)}
                      data-testid="candidate-profile-admin-remove-pool"
                    >
                      {busyPoolId === m.pool_id ? 'Removing…' : 'Remove from pool'}
                    </button>
                  ) : (
                    <span className="text-[10px] text-dark-text-secondary shrink-0">view only</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="Review">
        <div className="space-y-2 text-sm">
          <LabelValue label="Status">
            <ReviewStatusChip status={details?.reviewStatus ?? 'unreviewed'} />
          </LabelValue>
          {details?.reviewNotes && (
            <LabelValue label="Notes">
              <span className="text-dark-text whitespace-pre-wrap">{details.reviewNotes}</span>
            </LabelValue>
          )}
        </div>
      </Section>

      <Section title="Parse confidence">
        {confidenceEntries.length > 0 ? (
          <ul
            className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm"
            data-testid="candidate-profile-admin-confidence"
          >
            {confidenceEntries.map(([field, conf]) => (
              <li key={field} className="flex justify-between gap-2">
                <span className="text-dark-text-secondary">{field}</span>
                <span
                  className={`font-mono ${
                    typeof conf === 'number' && conf < 0.8 ? 'text-amber-400' : 'text-dark-text'
                  }`}
                >
                  {typeof conf === 'number' ? conf.toFixed(2) : String(conf)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-dark-text-secondary">No per-field confidence recorded.</p>
        )}
      </Section>

      <Section title="Audit">
        <dl
          className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm"
          data-testid="candidate-profile-admin-audit"
        >
          <dt className="text-xs text-dark-text-secondary">Profile updated</dt>
          <dd className="text-dark-text">
            {person.ProfileUpdatedAt
              ? formatDateIso(person.ProfileUpdatedAt) ?? person.ProfileUpdatedAt
              : '—'}
            {person.ProfileUpdatedBy ? ` · by ${person.ProfileUpdatedBy}` : ''}
          </dd>
          <dt className="text-xs text-dark-text-secondary">Record updated</dt>
          <dd className="text-dark-text">
            {person.RecordUpdatedAt
              ? formatDateIso(person.RecordUpdatedAt) ?? person.RecordUpdatedAt
              : '—'}
          </dd>
          <dt className="text-xs text-dark-text-secondary">PersonUUID</dt>
          <dd className="text-dark-text font-mono text-xs">{person.PersonUUID ?? '—'}</dd>
          <dt className="text-xs text-dark-text-secondary">Profile category</dt>
          <dd className="text-dark-text">{person.ProfileCategory ?? '—'}</dd>
        </dl>
      </Section>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h3 className="text-xs font-bold uppercase tracking-wider text-blue-300 mb-2">{title}</h3>
    {children}
  </section>
);

const LabelValue: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-dark-text-secondary w-24 shrink-0">{label}</span>
    <span>{children}</span>
  </div>
);

const ReviewStatusChip: React.FC<{ status: ReviewStatus }> = ({ status }) => {
  const map: Record<ReviewStatus, { label: string; cls: string }> = {
    unreviewed: {
      label: 'unreviewed',
      cls: 'bg-slate-800 border border-dark-border text-dark-text-secondary',
    },
    needs_review: {
      label: 'needs_review',
      cls: 'bg-amber-700 border border-amber-500/70 text-white',
    },
    reviewed: {
      label: 'reviewed',
      cls: 'bg-emerald-900/50 border border-emerald-500/60 text-dark-text',
    },
  };
  const style = map[status];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.cls}`}
    >
      {style.label}
    </span>
  );
};

function formatDateIso(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
